import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import * as Yup from 'yup';
import { useSearchParams } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import {
  Stack,
  TextField,
  Box,
  Button,
  Alert,
  InputAdornment,
  Typography,
  Divider,
  Chip,
  Tab
} from '@mui/material';
import { TabContext, TabList, TabPanel, LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { EventContext } from '../../../contexts/Event';
// services
import { createEvent, updateEvent } from '../../../services/Event';
import { updateSectionMany } from '../../../services/Section';
import { updateTicketMany } from '../../../services/Ticket';
import { updateInventoryMany } from '../../../services/Inventory';
import { updateVoucherMany } from '../../../services/Voucher';
import { updateSettingsRoute } from '../../../services/Config';

EventFormBasic.propTypes = {
  event: PropTypes.object,
  handleNext: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

export default function EventFormBasic({ event, handleNext, edit }) {
  const { authUser } = useAuth();
  const { authSettings, settingsId } = useContext(SettingsContext);
  const { setEvent } = useContext(EventContext);

  const [hasRouteError, setHasRouteError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRouteDisabled, setIsRouteDisabled] = useState(Boolean(event?.route?.subdomain));

  const eventBasicValues = {
    name: event?.name || '',
    description: event?.description || '',
    route: {
      subdomain: event?.route?.subdomain || ''
    },
    translation: {
      pt: {
        name: event?.translation?.pt?.name || '',
        description: event?.translation?.pt?.description || ''
      }
    }
  };

  // TODO: Melhorar essa validação
  const [searchParams, setSearchParams] = useSearchParams();

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const EventBasicSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    route: Yup.object({
      subdomain: Yup.string().required('Subdomain is required')
    }),
    translation: Yup.object({
      pt: Yup.object({
        name: Yup.string(),
        description: Yup.string()
      })
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: eventBasicValues,
    validationSchema: EventBasicSchema,
    onSubmit: async (values) => {
      setHasRouteError(false);
      setIsSubmitted(false);
      setIsLoading(true);

      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };
        const payload = {
          name: values.name,
          description: values.description,
          translation: {
            pt: {
              name: values.translation.pt.name,
              description: values.translation.pt.description
            }
          }
        };

        if (searchParams.get('eventId')) {
          await updateEvent({ headers, payload, id: event._id });
          setEvent((event) => ({ ...event, ...payload }));

          // TODO: Atualizar usando kafka (provisório)
          const payloadServices = {
            event: {
              _id: event._id,
              name: values.name,
              translation: {
                pt: {
                  name: values.translation.pt.name
                }
              }
            }
          };
          await updateSectionMany({ headers, payload: payloadServices });
          await updateTicketMany({ headers, payload: payloadServices });
          await updateInventoryMany({ headers, payload: payloadServices });
          await updateVoucherMany({ headers, payload: payloadServices });
        } else {
          payload.bookingType = searchParams.get('bookingType');
          payload.route = { subdomain: values.route.subdomain };

          const newEvent = await createEvent({ headers, payload });
          setSearchParams({ eventId: newEvent._id });

          // TODO: Atualizar usando kafka (provisório)
          const payloadSettingsServices = {
            route: {
              event: {
                subdomain: values.route.subdomain
              }
            }
          };
          await updateSettingsRoute({ headers, payload: payloadSettingsServices, id: settingsId });
          setIsRouteDisabled(true);
        }

        setIsLoading(false);

        if (!edit) {
          handleNext();
        } else {
          setIsSubmitted(true);
        }
      } catch (err) {
        setIsLoading(false);
        // TODO: Melhorar validação
        if (err.message === 'duplicated') {
          setHasRouteError(true);
        } else {
          console.error(err);
          alert('An error occurred! Please, try again.');
        }
      }
    }
  });

  const { errors, touched, handleSubmit, getFieldProps } = formik;

  return (
    <Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="[EN] Name*"
              {...getFieldProps('name')}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
            />

            <TextField
              fullWidth
              label="[EN] Description (optional)"
              multiline
              rows={4}
              {...getFieldProps('description')}
              error={Boolean(touched.description && errors.description)}
              helperText={touched.description && errors.description}
            />

            <Divider>
              <Chip label="Translations" size="small" />
            </Divider>

            <Box sx={{ width: '100%', typography: 'body1', marginTop: '0 !important' }}>
              <TabContext value="pt">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TabList>
                    <Tab label="PT" value="pt" />
                  </TabList>
                </Box>
                <TabPanel value="pt">
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="[PT] Name"
                      variant="filled"
                      {...getFieldProps('translation.pt.name')}
                      error={Boolean(touched.translation?.pt?.name && errors.translation?.pt?.name)}
                      helperText={touched.translation?.pt?.name && errors.translation?.pt?.name}
                    />

                    <TextField
                      fullWidth
                      label="[PT] Description (optional)"
                      multiline
                      variant="filled"
                      rows={4}
                      {...getFieldProps('translation.pt.description')}
                      error={Boolean(
                        touched.translation?.pt?.description && errors.translation?.pt?.description
                      )}
                      helperText={
                        touched.translation?.pt?.description && errors.translation?.pt?.description
                      }
                    />
                  </Stack>
                </TabPanel>
              </TabContext>
            </Box>

            <Typography component="h2" variant="h6">
              Event URL
              <Typography
                component="span"
                variant="body2"
                color="grey.500"
                sx={{ display: 'block' }}
              >
                This will be the url to access the event
              </Typography>
            </Typography>

            <Alert severity="warning" sx={{ maxWidth: '550px' }}>
              <strong>IMPORTANT</strong> Once you save your subdomain, you will not be able to edit!
            </Alert>

            <TextField
              fullWidth
              label="Subdomain*"
              {...getFieldProps('route.subdomain')}
              error={Boolean(touched.route?.subdomain && errors.route?.subdomain)}
              helperText={touched.route?.subdomain && errors.route?.subdomain}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {process.env.REACT_APP_STAGE !== 'prod' && `.${process.env.REACT_APP_STAGE}`}.
                    {process.env.REACT_APP_DOMAIN}
                    {process.env.REACT_APP_STAGE === 'local' &&
                      `:${process.env.REACT_APP_CLIENT_EVENT_PORT}`}
                  </InputAdornment>
                )
              }}
              disabled={isRouteDisabled}
              sx={{ maxWidth: '550px' }}
            />

            {hasRouteError && (
              <Alert severity="error">Subdomain already exist! Choose another!</Alert>
            )}

            <Box>
              {event && !edit && (
                <Button size="large" variant="outlined" onClick={handleNext} sx={{ mr: 1 }}>
                  Next
                </Button>
              )}
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isLoading}
                endIcon={isSubmitted && <Icon icon={checkmarkCircleFill} />}
              >
                Save
              </LoadingButton>
            </Box>
          </Stack>
        </Form>
      </FormikProvider>
    </Box>
  );
}
